// @greenlight-spec: 48-user-passthrough.md#test-sign-in
import {
  ENTRA_SIGN_IN_TEST_HINTS,
  SIGN_IN_TEST_REDIRECT_TOKEN,
  signInTestHintFor,
  signInTestHintsFor,
  type SignInTestHint,
} from '@greenlight/shared';

/** The same-origin channel the result tab reports on to the dashboard tab that opened it. */
export const SIGN_IN_TEST_CHANNEL = 'greenlight-sign-in-test';

/** How long the dashboard waits before showing the failures that never come back. */
export const SIGN_IN_TEST_STALL_MS = 60_000;

export interface SignInTestTarget {
  integrationId: string;
  credentialSlug: string;
  /** Picks the provider's hints; a Custom registration falls back to its authorize URL. */
  catalogKey: string | null;
  authorizeUrl: string | null;
}

export type SignInTestOutcome = 'verified' | 'failed' | 'refused' | 'declined';

export interface SignInTestMessage {
  integrationId: string;
  credentialSlug: string;
  outcome: SignInTestOutcome;
  account: string | null;
  reason: string | null;
  code: string | null;
}

export const signInTestKey = (t: { integrationId: string; credentialSlug: string }): string =>
  `${t.integrationId}/${t.credentialSlug}`;

export function signInTestUrl(t: { integrationId: string; credentialSlug: string }): string {
  const q = new URLSearchParams({
    integration: t.integrationId,
    credential: t.credentialSlug,
    test: '1',
  });
  return `/connect/start?${q.toString()}`;
}

export function hintsFor(t: Pick<SignInTestTarget, 'catalogKey' | 'authorizeUrl'>) {
  return signInTestHintsFor(t.catalogKey, t.authorizeUrl);
}

/** A hint's text with the install's connect callback in place of `{redirect_uri}`. */
export function withRedirectUri(text: string, origin = window.location.origin): string {
  return text.split(SIGN_IN_TEST_REDIRECT_TOKEN).join(`${origin}/connect/callback`);
}

export function parseSignInTestMessage(data: unknown): SignInTestMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
  const outcome = d['outcome'];
  if (
    typeof d['integrationId'] !== 'string' ||
    typeof d['credentialSlug'] !== 'string' ||
    (outcome !== 'verified' &&
      outcome !== 'failed' &&
      outcome !== 'refused' &&
      outcome !== 'declined')
  ) {
    return null;
  }
  return {
    integrationId: d['integrationId'],
    credentialSlug: d['credentialSlug'],
    outcome,
    account: str(d['account']),
    reason: str(d['reason']),
    code: str(d['code']),
  };
}

interface FailureCopy {
  cause: string;
  fix: string;
}

const DECLINED: FailureCopy = {
  cause: 'You declined the consent screen, so nothing was connected.',
  fix: 'Test again and accept. Each person sees that screen too unless an administrator consents for the organization (on Entra: Grant admin consent).',
};

/** Greenlight's own reasons a test failed, phrased for the IT admin who ran it. */
const FAILURES: Record<string, FailureCopy> = {
  oid_mismatch: {
    cause:
      'You finished the sign-in as a different Microsoft account than the one you use for Greenlight.',
    fix: 'Sign out of other Microsoft accounts in this browser, or pick your own, and test again.',
  },
  tenant_mismatch: {
    cause: 'The account you signed in with belongs to another Microsoft tenant.',
    fix: "Check that the integration's tenant id is your organization's Directory (tenant) ID, and sign in with your work account.",
  },
  oid_unrecorded: {
    cause: 'Greenlight has not recorded which Microsoft account is yours.',
    fix: 'Sign out of Greenlight, sign in again, and test again.',
  },
  profile_scope_missing: {
    cause:
      'Your organization’s Greenlight sign-in does not request the profile scope, so nobody’s Microsoft account can be matched.',
    fix: 'Add profile to the identity provider’s sign-in scopes in Settings, then sign in again.',
  },
  username_mismatch: {
    cause: 'The username the provider returned is not your Greenlight email.',
    fix: 'On Snowflake, set your user’s LOGIN_NAME to the email you sign in to Greenlight with; elsewhere, sign in with that account.',
  },
  username_missing: {
    cause: 'The provider did not say which account signed in.',
    fix: "Check that the integration's token URL is the provider's OAuth token endpoint.",
  },
  email_mismatch: {
    cause: 'The account’s email is not your Greenlight email.',
    fix: 'Sign in to the provider with the account that uses your Greenlight email.',
  },
  email_unverified: {
    cause: 'The provider has not verified that account’s email.',
    fix: 'Verify the email at the provider, then test again.',
  },
  email_missing: {
    cause: 'The provider returned no email for the account.',
    fix: 'Add openid and email to the integration’s scopes.',
  },
  id_token_missing: {
    cause: 'The provider returned no identity for the account.',
    fix: 'Add openid to the integration’s scopes (openid profile offline_access on Entra).',
  },
  id_token_invalid: {
    cause: 'The provider’s identity token could not be verified.',
    fix: "Check that the integration's authorize and token URLs belong to the same issuer.",
  },
  token_exchange_failed: {
    cause: 'The provider refused to redeem the sign-in.',
    fix: "Check the credential's client id and replace its secret with a current one.",
  },
  state_mismatch: {
    cause: 'The test was finished from a different Greenlight session than it started in.',
    fix: 'Test again from this tab.',
  },
  state_expired: {
    cause: 'The test took longer than five minutes, or its tab lost its cookie.',
    fix: 'Test again.',
  },
  integration_gone: {
    cause: 'The integration or credential was removed while the test ran.',
    fix: 'Reload the page.',
  },
};

/** Why a test failed and how to fix it: the provider's code first, then Greenlight's reason. */
export function signInTestFailure(
  message: Pick<SignInTestMessage, 'outcome' | 'reason' | 'code'>,
  hints: readonly SignInTestHint[],
): FailureCopy & { code: string | null } {
  if (message.outcome === 'declined') return { ...DECLINED, code: null };
  // A Custom registration's result page knows no authorize URL; an Entra code still names Entra.
  const hint =
    signInTestHintFor(hints, message.code) ??
    (message.code?.startsWith('AADSTS')
      ? signInTestHintFor(ENTRA_SIGN_IN_TEST_HINTS, message.code)
      : undefined);
  if (hint) return { cause: hint.cause, fix: withRedirectUri(hint.fix), code: hint.code };
  const known = FAILURES[message.reason ?? ''];
  if (known) return { ...known, code: message.code };
  return {
    cause: `The provider answered ${message.code ?? message.reason ?? 'with an error'}.`,
    fix: 'Compare the registration with the setup guide, then test again.',
    code: message.code,
  };
}
